import { subWeeks } from 'date-fns'
import { getLocale } from '@/utils/date-fns'
import { EXPIRATION_TYPES } from './consts'
import type { EditVehicleServicePlanForm } from './types'
import type {
  VehicleExpirationNode,
  VehicleServicePlanMutationInput,
} from './types.graphql'

export const getVehicleExpirationTypeName = ({
  expiration,
  name,
  fallback = '-',
}: {
  expiration?: VehicleExpirationNode
  name?: string
  fallback?: string
}): string => {
  const resolvedName = name ?? expiration?.expirationType?.name
  if (!resolvedName) return fallback
  return EXPIRATION_TYPES[resolvedName] ?? resolvedName ?? fallback
}

/**
 * The service plan modal's form as mutation input. The notification time is
 * stored as an absolute datetime but entered as a number of weeks before the
 * visit, and both the responsible user and the time are dropped when the
 * notification checkbox is off.
 */
export function getVehicleServicePlanInput(
  form: EditVehicleServicePlanForm,
  hasNotification: boolean
): VehicleServicePlanMutationInput {
  return {
    name: form.name,
    description: form.description,
    scheduledStart: form.scheduledStart,
    scheduledEnd: form.scheduledEnd,
    status: form.status,
    workshop: form.workshop,
    serviceTypes: form.serviceTypes,
    cost: form.cost,
    timeSpent: form.timeSpent,
    responsibleUser: hasNotification ? form.responsibleUser : null,
    notificationTime: hasNotification
      ? subWeeks(form.scheduledStart, form.notificationTime)
      : null,
  }
}

/**
 * A stored time spent split into the hour and minute parts the service modal
 * edits. Both parts are `undefined` when nothing is registered, so an untouched
 * pair of inputs stays empty instead of showing a zero.
 */
export function splitTimeSpent(timeSpent: number | null): {
  hours: number | undefined
  minutes: number | undefined
} {
  if (timeSpent === null) return { hours: undefined, minutes: undefined }
  return { hours: Math.floor(timeSpent / 60), minutes: timeSpent % 60 }
}

/**
 * The hour and minute inputs back as one number of minutes -- `null` when both
 * are empty, so clearing the pair unregisters the time rather than storing a
 * zero.
 */
export function combineTimeSpent(
  hours: number | undefined,
  minutes: number | undefined
): number | null {
  if (hours === undefined && minutes === undefined) return null
  return (hours ?? 0) * 60 + (minutes ?? 0)
}

/** A time spent as `HH:MM`, the same shape the services export writes. */
export function formatTimeSpent(timeSpent: number | null): string {
  if (timeSpent === null) return '-'
  const { hours, minutes } = splitTimeSpent(timeSpent)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * A registered cost in whole NOK, the same shape the issue table shows a cost
 * in. The app has no per-company currency.
 */
export function formatCost(cost: number | null): string {
  if (cost === null) return '-'
  return `${cost.toLocaleString(getLocale().code)} NOK`
}

/**
 * Integration rows (Scania) name their workshop as free text; services
 * registered in TaCtrl point at one from the company's workshop register.
 */
export function getServicePlanWorkshopName(servicePlan: {
  workshop?: { name: string } | null
  workshopName?: string | null
}): string {
  return servicePlan.workshop?.name || servicePlan.workshopName || ''
}

export function getServicePlanTypeNames(
  serviceTypes: { name: string }[] | null | undefined
): string {
  return (serviceTypes ?? [])
    .map(type => getVehicleExpirationTypeName({ name: type.name }))
    .join(', ')
}
